/**
 * The engine workspace's part of Radar's phone home screen (docs/PLAN-2026-09-21-mobile-os-shell.md §3): the reads
 * the Reports (flags), Schools and Cockpit windows already make, under the same query keys so the window opens on data
 * the home screen already has, and the lines they make. It lives with the engine apps because it reads the engine
 * (the track fence: only src/apps/engine touches @gamo/*). No new API.
 *
 * Every read is asked only with the permission its route checks, so a staff member without it makes no request and
 * sees no card. The engine's D1 has a daily read ceiling (2026-09-17): these are the windows' own first pages, read
 * once and cached, with no timer.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { FlagRow, SchoolRow } from "@gamo/api-client";
import { engine } from "../../engine-api";

/** The Reports window's list (Flags.tsx): the waiting queue, at most this many rows. */
export const FLAGS_PAGE = 150;

export interface EnginePhoneData {
  flags: FlagRow[] | null;
  flagsFull: boolean;
  schools: SchoolRow[] | null;
  activeToday: number | null;
}

export function useEnginePhoneData(on: { flags: boolean; schools: boolean; stats: boolean }): EnginePhoneData {
  const flags = useQuery({ queryKey: ["engine", "flags", "waiting"], queryFn: () => engine.admin.flags({ reviewed: "waiting", limit: FLAGS_PAGE }), enabled: on.flags, retry: false });
  const schools = useInfiniteQuery({
    queryKey: ["engine", "schools", ""],
    queryFn: ({ pageParam }) => engine.admin.orgs.list(pageParam ? { before: pageParam } : {}),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_before ?? undefined,
    enabled: on.schools,
    retry: false,
  });
  // Only when there is no queue to count: the Cockpit's own numbers, the heaviest of the three.
  const stats = useQuery({ queryKey: ["engine", "stats"], queryFn: () => engine.admin.stats(), enabled: on.stats, retry: false });
  const flagRows = on.flags ? (flags.data?.flags ?? null) : null;
  return {
    flags: flagRows,
    flagsFull: (flagRows?.length ?? 0) >= FLAGS_PAGE,
    schools: on.schools ? (schools.data?.pages.flatMap((p) => p.schools) ?? null) : null,
    activeToday: on.stats ? (stats.data?.learners.active_today ?? null) : null,
  };
}

/** Schools that need the founder to look: the main admin has not used the login yet, or the school is paused. */
export function schoolsNeedingLook(rows: Array<Pick<SchoolRow, "status">>): number {
  return rows.filter((r) => r.status === "never_signed_in" || r.status === "paused").length;
}

export function schoolsWidgetLine(rows: Array<Pick<SchoolRow, "status">>): string {
  if (rows.length === 0) return "No schools yet";
  const unused = rows.filter((r) => r.status === "never_signed_in").length;
  const paused = rows.filter((r) => r.status === "paused").length;
  if (unused + paused === 0) return rows.length === 1 ? "1 school, in use" : `${rows.length} schools, all in use`;
  const parts = [unused ? `${unused} with an unused login` : "", paused ? `${paused} paused` : ""].filter(Boolean);
  return parts.join(", ");
}

export function flagsCount(n: number, full: boolean): string {
  return full ? `${n} or more` : String(n);
}

export function flagsWidgetLine(n: number, full: boolean): string {
  if (n === 0) return "None waiting";
  return `${flagsCount(n, full)} for a person to read`;
}

export function flagsStatLine(n: number, full: boolean): string {
  if (n === 0) return "No reports waiting";
  return n === 1 ? "1 report to review" : `${flagsCount(n, full)} reports to review`;
}

export function activeTodayLine(n: number): string {
  return n === 1 ? "1 learner active today" : `${n} learners active today`;
}

/** What Search finds among the waiting reports already on hand: the question, the note, the learner, the skill. */
export function flagSearchText(f: FlagRow): string {
  return [f.item?.stem ?? "", f.note ?? "", f.analytics_id ?? "", f.item?.objective_code ?? ""].join(" ");
}
